import { formatMoney } from '../../../../../utils/utils';

export const AMENITY_COST_TYPE = {
  NONE: 0,
  HOURLY: 1,
  FLAT: 2,
  DAILY: 3
};

const PAYMENT_AMOUNT_DECIMALS = 2;

export function getRateTypeOptions(allowMultiDay, t) {
  const td = (key, fallback) => t(key, { defaultValue: fallback });
  const options = [
    {
      value: AMENITY_COST_TYPE.HOURLY,
      label: td('add_amenity_rate_type_hourly', 'Hourly Rate')
    },
    {
      value: AMENITY_COST_TYPE.FLAT,
      label: td('add_amenity_rate_type_flat', 'Flat Rate')
    }
  ];

  if (allowMultiDay) {
    options.splice(1, 0, {
      value: AMENITY_COST_TYPE.DAILY,
      label: td('add_amenity_rate_type_daily', 'Daily Rate')
    });
  }

  return options;
}

export function getRateTypeLabel(costType, t) {
  const td = (key, fallback) => t(key, { defaultValue: fallback });
  switch (Number(costType)) {
    case AMENITY_COST_TYPE.HOURLY:
      return td('add_amenity_rate_type_hourly', 'Hourly Rate');
    case AMENITY_COST_TYPE.FLAT:
      return td('add_amenity_rate_type_flat', 'Flat Rate');
    case AMENITY_COST_TYPE.DAILY:
      return td('add_amenity_rate_type_daily', 'Daily Rate');
    default:
      return '—';
  }
}

export function getRateAmountLabel(costType, t) {
  const td = (key, fallback) => t(key, { defaultValue: fallback });
  switch (Number(costType)) {
    case AMENITY_COST_TYPE.HOURLY:
      return td('add_amenity_rate_per_hour', 'Rate per Hour');
    case AMENITY_COST_TYPE.FLAT:
      return td('add_amenity_booking_fee', 'Booking Fee');
    case AMENITY_COST_TYPE.DAILY:
      return td('add_amenity_rate_per_day', 'Rate per Day');
    default:
      return td('add_amenity_rate_amount', 'Rate');
  }
}

export function getBookingFeeSuffix(costType) {
  switch (Number(costType)) {
    case AMENITY_COST_TYPE.HOURLY:
      return '/ hour';
    case AMENITY_COST_TYPE.DAILY:
      return '/ day';
    case AMENITY_COST_TYPE.FLAT:
      return '/ booking';
    default:
      return '';
  }
}

/** List/CSV label: "Free" or "$12.00 / hour" (etc.). */
export function formatAmenityFeeDisplay(amenity, t) {
  const freeLabel = t ? t('Free', { defaultValue: 'Free' }) : 'Free';
  if (!amenity?.require_payment) {
    return freeLabel;
  }
  const cost = amenity.cost ?? amenity.payment_rate;
  const n = parsePaymentAmount(cost);
  if (n == null || n <= 0) {
    return '—';
  }
  const suffix = getBookingFeeSuffix(amenity.cost_type);
  return suffix ? `${formatMoney(n)} ${suffix}` : formatMoney(n);
}

export function isValidCostTypeForBooking(costType, allowMultiDay) {
  const n = Number(costType);
  if (n === AMENITY_COST_TYPE.HOURLY || n === AMENITY_COST_TYPE.FLAT) return true;
  if (n === AMENITY_COST_TYPE.DAILY) return !!allowMultiDay;
  return false;
}

export function parsePaymentAmount(value) {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  if (!Number.isFinite(n)) return null;
  return n;
}

export function formatOptionalMoneyOrNone(amount, t) {
  const n = parsePaymentAmount(amount);
  if (n == null || n <= 0) {
    return t('None', { defaultValue: 'None' });
  }
  return formatMoney(n);
}

export function parsePaymentAmountForPayload(value) {
  const parsed = parsePaymentAmount(value);
  return parsed != null ? parsed : 0;
}

export function formatPaymentAmountForDisplay(value) {
  if (value === null || value === undefined || value === '') return '';
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  return String(value);
}

export function hasAtMostDecimalPlaces(value, maxDecimals = PAYMENT_AMOUNT_DECIMALS) {
  if (value === null || value === undefined || value === '') return true;
  const str = String(value).trim();
  if (!str) return true;
  const parts = str.split('.');
  if (parts.length === 1) return true;
  return parts[1].length <= maxDecimals;
}

export function buildPaymentFieldsFromApi(apiData) {
  const requirePayment = !!apiData?.require_payment;
  const costType =
    apiData?.cost_type != null ? Number(apiData.cost_type) : AMENITY_COST_TYPE.NONE;
  const cost = apiData?.cost != null ? Number(apiData.cost) : null;
  const serviceCharge =
    apiData?.service_charge != null ? Number(apiData.service_charge) : null;
  const deposit = apiData?.deposit != null ? Number(apiData.deposit) : null;

  if (!requirePayment) {
    return {
      cost_type: AMENITY_COST_TYPE.NONE,
      payment_rate: null,
      service_charge: null,
      deposit: null
    };
  }

  const allowMultiDay = !!apiData?.allow_multi_day_bookings;
  const resolvedCostType = isValidCostTypeForBooking(costType, allowMultiDay)
    ? costType
    : AMENITY_COST_TYPE.NONE;

  return {
    cost_type: resolvedCostType,
    payment_rate: cost != null && !Number.isNaN(cost) ? cost : null,
    service_charge:
      serviceCharge != null && !Number.isNaN(serviceCharge) && serviceCharge > 0
        ? serviceCharge
        : null,
    deposit: deposit != null && !Number.isNaN(deposit) && deposit > 0 ? deposit : null
  };
}

export const AMENITY_PAYMENT_FORM_FIELDS = [
  'cost_type',
  'payment_rate',
  'service_charge',
  'deposit'
];

export function resetPaymentFormDefaults() {
  return {
    cost_type: AMENITY_COST_TYPE.NONE,
    payment_rate: null,
    service_charge: null,
    deposit: null
  };
}

export function clearPaymentFormFields(setValue, clearErrors, options = {}) {
  const { shouldDirty = true, shouldValidate = false } = options;
  Object.entries(resetPaymentFormDefaults()).forEach(([key, val]) => {
    setValue(key, val, { shouldDirty, shouldValidate });
  });
  if (clearErrors) {
    clearErrors(AMENITY_PAYMENT_FORM_FIELDS);
  }
}

export function handleRequirePaymentUserChange(nextValue, fieldOnChange, { setValue, clearErrors }) {
  fieldOnChange(nextValue);
  clearPaymentFormFields(setValue, clearErrors);
}

export function disableRequirePayment({ setValue, clearErrors }, options = {}) {
  setValue('require_payment', false, { shouldDirty: true, shouldValidate: true });
  clearPaymentFormFields(setValue, clearErrors, options);
}

export function isUnsetCostType(costType) {
  return Number(costType) === AMENITY_COST_TYPE.NONE;
}
